import {yupResolver} from "@hookform/resolvers/yup";
import {Button, Checkbox, FormControlLabel, FormGroup, FormLabel, Stack, TextField, useTheme} from "@mui/material";
import {useForm} from "react-hook-form";
import {useNavigate} from "react-router-dom";
import * as yup from "yup";
import useApiRequest from "../../hooks/useApiRequest";
import {snackbar} from "../../utils/snackbar";

const GRANTS_OPTIONS = ["authorization_code", "password", "client_credentials", "refresh_token"];

const schema = yup.object().shape({
  name: yup
    .string()
    .required("El nombre es obligatorio")
    .max(255, "Máximo 255 caracteres")
    .matches(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\- ]*$/, "Solo se permiten letras y espacios"),

  allowed_grants: yup.array().required("Allowed grants es obligatorio"),

  redirect_uris: yup
    .mixed()
    .transform((value) => {
      if (Array.isArray(value)) {
        return value.map((uri) => (typeof uri === "string" ? uri.replace(/^"+|"+$/g, "").trim() : uri)).join(", ");
      }
      if (typeof value === "string") {
        return value.replace(/^"+|"+$/g, "").trim();
      }
      return "";
    })
    .required("Los redirect URIs son obligatorios")
    .test("is-valid-urls", "Introduce URLs válidas separadas por comas", (value) => {
      if (!value || typeof value !== "string") return false;
      return /^(https?:\/\/[^\s,]+(\.[^\s,]+)*(\/[^\s,?&=]*)?(\?[^\s,&]*(&[^\s,&]*)*)?)((\s*,\s*https?:\/\/[^\s,]+(\.[^\s,]+)*(\/[^\s,?&=]*)?(\?[^\s,&]*(&[^\s,&]*)*)?)*)$/.test(
        value
      );
    })
});

const formatRedirectUris = (redirect_uris) => {
  if (Array.isArray(redirect_uris)) {
    return redirect_uris.map((uri) => (typeof uri === "string" ? uri.replace(/^"+|"+$/g, "").trim() : uri)).join(", ");
  }
  if (typeof redirect_uris === "string") {
    return redirect_uris.replace(/^"+|"+$/g, "").trim();
  }
  return "";
};

const parseRedirectUris = (uriString) => {
  return uriString
    .split(",")
    .map((uri) => uri.replace(/^"+|"+$/g, "").trim())
    .filter((uri) => uri.length > 0);
};

const ClientForm = ({isNew, refreshData, setIsEditing, initialClientData}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const {create, update} = useApiRequest({
    baseUrl: `/clients`,
    resource: "clients"
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: {errors}
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...initialClientData,
      allowed_grants: initialClientData?.allowed_grants || [],
      redirect_uris: formatRedirectUris(initialClientData?.redirect_uris)
    },
    context: {isNew}
  });

  const allowed_grants = watch("allowed_grants");

  const handleGrantChange = (grant) => (event) => {
    const checked = event.target.checked;
    let newGrants = Array.isArray(allowed_grants) ? [...allowed_grants] : [];

    if (checked) {
      newGrants.push(grant);
    } else {
      newGrants = newGrants.filter((g) => g !== grant);
    }

    setValue("allowed_grants", newGrants, {shouldValidate: true});
  };

  const handleSave = async (data) => {
    const dataToSend = {
      name: data.name,
      allowed_grants: data.allowed_grants,
      redirect_uris: parseRedirectUris(data.redirect_uris)
    };

    if (!isNew) {
      dataToSend.client_id = initialClientData.client_id;
    }

    try {
      let result;
      if (isNew) {
        result = await create(dataToSend);

        if (result?.success && result?.response?.data) {
          const {client_id, client_secret} = result.response.data;

          snackbar.copy("¡Cliente creado correctamente!", {
            persist: true,
            fields: [
              {label: "Client ID", value: client_id},
              {label: "Client Secret", value: client_secret.slice(0, 26) + "...", copyValue: client_secret}
            ],
            warning: "Solo puedes copiar Client Secret en este momento."
          });

          navigate("/clients");
          return;
        }
      } else {
        result = await update(initialClientData.client_id, dataToSend);
      }

      if (result?.success) {
        refreshData();
        setIsEditing(false);
        if (isNew && result.data?.id) {
          navigate("/client-form", {state: {id: result.data?.id}});
        }
      }
    } catch (error) {
      console.error("Error al guardar cambios:", error);
    }
  };

  const onSubmit = (data) => {
    handleSave(data);
  };

  return (
    <Stack direction="column" spacing={2} minWidth={"300px"}>
      <TextField
        label="Nombre"
        {...register("name")}
        slotProps={{htmlInput: {maxLength: 255}}}
        error={!!errors.name}
        helperText={errors.name?.message}
        variant="outlined"
      />

      <TextField
        label="Redirect URIs"
        {...register("redirect_uris")}
        error={!!errors.redirect_uris}
        helperText={errors.redirect_uris?.message}
        variant="outlined"
      />

      <FormLabel>Allowed Grants</FormLabel>
      <FormGroup column>
        {GRANTS_OPTIONS.map((grant) => (
          <FormControlLabel
            key={grant}
            style={{color: theme.palette.text.primary}}
            control={
              <Checkbox
                checked={allowed_grants?.includes(grant) || false}
                onChange={handleGrantChange(grant)}
                style={{color: theme.palette.colors.secondary}}
              />
            }
            label={grant}
          />
        ))}
      </FormGroup>
      {errors.allowed_grants && <span style={{color: "red", fontSize: "0.9em"}}>{errors.allowed_grants.message}</span>}

      <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)}>
        {isNew ? "Crear" : "Guardar"}
      </Button>
    </Stack>
  );
};

export default ClientForm;
